/**
 * useTwoSegmentResolver - DB-First Two-Segment Resolution
 * 
 * Resolves two-segment URLs (/:segment1/:segment2) via real-time database queries.
 * 
 * Patterns:
 * 1. Route-with-child: /topics/:slug, /themen/:slug → topic detail
 *    - Locale authority: Route prefix (segment1)
 * 2. Account subroute: /account/:subroute → static subroutes
 *    - Locale authority: Account prefix (segment1)
 * 3. Expert-publication: /:vanityUrl/:publicationSlug → publication detail
 *    - Locale authority: Publication slug (segment2)
 * 
 * INV-4: Language switching uses sourceId to find translated slugs
 * INV-7: Topic slug is passed in metadata for language switching
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { isExpertVanityUrl } from '@/lib/routing/expertLookup';
import { ACCOUNT_SUBROUTES } from '@/lib/routing/types';
import { getPreferredLocale, selectByLocalePriority } from '@/lib/routing/localePriority';
import { logger } from '@/utils/logger';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Two-segment resolution result types
 */
export type TwoSegmentResolution =
  // Route-based patterns: /topics/:slug, /themen/:slug
  | { 
      type: 'route-with-child';
      routeSourceId: string;        // 'topics'
      routeSlug: string;            // Translated route slug (for URL)
      childSourceType: 'topic';     // Child entity type
      childSourceId: string;        // UUID of the topic
      childSlug: string;            // Topic slug
      locale: string;               // FROM THE ROUTE PREFIX
    }
  // Expert publication pattern: /:vanity-url/:publication-slug
  | {
      type: 'expert-publication';
      expertVanityUrl: string;      // Author's vanity URL (constant across locales)
      publicationSourceId: string;  // UUID of the publication
      publicationSlug: string;      // Translated publication slug
      locale: string;               // FROM THE PUBLICATION SLUG
    }
  // Account subroutes: /account/settings, /konto/einstellungen
  | {
      type: 'account-subroute';
      routeSlug: string;            // Translated account route slug
      subroute: string;             // Static subroute (settings, profile, etc.)
      locale: string;               // FROM THE ACCOUNT PREFIX
    }
  // Location + global route redirect: /spain/experts → /experts
  | {
      type: 'location-route-redirect';
      targetPath: string;           // Path to redirect to
    }
  | { type: 'notFound' };

/**
 * Hook return type
 */
export interface UseTwoSegmentResolverResult {
  /** Resolution result - undefined while loading, TwoSegmentResolution after */
  resolution: TwoSegmentResolution | undefined;
  isLoading: boolean;
  error: Error | null;
}

// ============================================================================
// CORE RESOLUTION FUNCTION
// ============================================================================

/**
 * Resolve a two-segment URL to entity + locale via DB query
 */
async function resolveTwoSegment(
  segment1: string,
  segment2: string
): Promise<TwoSegmentResolution> {
  const startTime = performance.now();

  if (!segment1 || !segment2) {
    return { type: 'notFound' };
  }

  try {
    // Step 1: Check if segment1 is a route prefix (topics, account, etc.)
    const { data: routeData, error: routeError } = await supabase
      .from('localization_metadata')
      .select('source_id, locale, translated_slug')
      .eq('translated_slug', segment1)
      .eq('source_type', 'route')
      .maybeSingle();

    if (routeError) {
      logger.warn('[useTwoSegmentResolver] Route query failed', { 
        segment1, 
        error: routeError.message 
      });
    }

    if (routeData) {
      const routeLocale = routeData.locale;

      // Topics route → look up topic in segment2
      if (routeData.source_id === 'topics') {
        const { data: topicData } = await supabase
          .from('localization_metadata')
          .select('source_id, translated_slug')
          .eq('translated_slug', segment2)
          .eq('source_type', 'topic')
          .maybeSingle();

        if (topicData) {
          if (process.env.NODE_ENV === 'development') {
            logger.debug('[useTwoSegmentResolver] Resolved topic', {
              segment1,
              segment2,
              routeLocale,
              topicSourceId: topicData.source_id,
              timeMs: Math.round(performance.now() - startTime),
            });
          }

          return {
            type: 'route-with-child',
            routeSourceId: 'topics',
            routeSlug: segment1,
            childSourceType: 'topic',
            childSourceId: topicData.source_id,
            childSlug: segment2,
            locale: routeLocale,
          };
        }

        // Topic not found but route matched - still return route context
        // This allows the TopicPage to show 404 appropriately
        return {
          type: 'route-with-child',
          routeSourceId: 'topics',
          routeSlug: segment1,
          childSourceType: 'topic',
          childSourceId: '', // Empty = not found
          childSlug: segment2,
          locale: routeLocale,
        };
      }

      // Account route → static subroute
      if (routeData.source_id === 'account') {
        // Validate against known subroutes
        const isValidSubroute = ACCOUNT_SUBROUTES.has(segment2);
        
        if (process.env.NODE_ENV === 'development') {
          logger.debug('[useTwoSegmentResolver] Resolved account subroute', {
            segment1,
            segment2,
            routeLocale,
            isValidSubroute,
            timeMs: Math.round(performance.now() - startTime),
          });
        }

        return {
          type: 'account-subroute',
          routeSlug: segment1,
          subroute: segment2,
          locale: routeLocale,
        };
      }
    }

    // Step 2: Check if segment1 is a location (for redirect detection)
    const { data: locationData } = await supabase
      .from('localization_metadata')
      .select('source_id')
      .eq('translated_slug', segment1)
      .eq('source_type', 'location')
      .maybeSingle();

    if (locationData) {
      // Check if segment2 is a global route → redirect to canonical URL
      const { data: segment2Route } = await supabase
        .from('localization_metadata')
        .select('translated_slug')
        .eq('translated_slug', segment2)
        .eq('source_type', 'route')
        .maybeSingle();

      if (segment2Route) {
        return {
          type: 'location-route-redirect',
          targetPath: `/${segment2}`,
        };
      }

      // Location + page → redirect to canonical
      const { data: segment2Page } = await supabase
        .from('localization_metadata')
        .select('translated_slug')
        .eq('translated_slug', segment2)
        .eq('source_type', 'page')
        .maybeSingle();

      if (segment2Page) {
        return {
          type: 'location-route-redirect',
          targetPath: `/${segment2}`,
        };
      }
    }

    // Step 3: Check if segment1 is an expert vanity URL
    const isExpert = await isExpertVanityUrl(segment1);
    
    if (isExpert) {
      // Look up publication by segment2 - fetch ALL matches for collision resolution
      const { data: publicationRows } = await supabase
        .from('localization_metadata')
        .select('source_id, locale, translated_slug')
        .eq('translated_slug', segment2)
        .eq('source_type', 'publication');

      if (publicationRows && publicationRows.length > 0) {
        // Apply locale priority to resolve collision (Stored → Browser → EN → First)
        const selectedPublication = selectByLocalePriority(publicationRows);

        if (process.env.NODE_ENV === 'development') {
          logger.debug('[useTwoSegmentResolver] Resolved expert publication', {
            segment1,
            segment2,
            publicationLocale: selectedPublication.locale,
            publicationSourceId: selectedPublication.source_id,
            candidateCount: publicationRows.length,
            timeMs: Math.round(performance.now() - startTime),
          });
        }

        return {
          type: 'expert-publication',
          expertVanityUrl: segment1,
          publicationSourceId: selectedPublication.source_id,
          publicationSlug: segment2,
          locale: selectedPublication.locale,
        };
      }

      // Expert exists but publication not found - could be 404 or publication slug variant
      // For now, return expert-publication with empty sourceId to let the page handle it
      return {
        type: 'expert-publication',
        expertVanityUrl: segment1,
        publicationSourceId: '',
        publicationSlug: segment2,
        locale: getPreferredLocale(),
      };
    }

    // Not found
    if (process.env.NODE_ENV === 'development') {
      logger.debug('[useTwoSegmentResolver] Not found', {
        segment1,
        segment2,
        timeMs: Math.round(performance.now() - startTime),
      });
    }

    return { type: 'notFound' };
  } catch (err) {
    logger.error('[useTwoSegmentResolver] Unexpected error', { 
      segment1, 
      segment2, 
      error: err 
    });
    return { type: 'notFound' };
  }
}

// Note: Locale priority helpers (getPreferredLocale, selectByLocalePriority)
// are imported from @/lib/routing/localePriority

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook to resolve a two-segment URL to entity + locale
 * 
 * @param segment1 - First URL segment (e.g., 'topics', 'john-smith')
 * @param segment2 - Second URL segment (e.g., 'beps', 'tax-guide')
 * @returns Resolution result with pattern type, entity IDs, and locale
 * 
 * @example
 * const { resolution, isLoading } = useTwoSegmentResolver(segment1, segment2);
 * if (resolution?.type === 'route-with-child') {
 *   // Render topic page with resolution.childSourceId
 * }
 */
export function useTwoSegmentResolver(
  segment1: string | undefined,
  segment2: string | undefined
): UseTwoSegmentResolverResult {
  const {
    data: resolution,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['two-segment-resolution', segment1, segment2],
    queryFn: () => resolveTwoSegment(segment1 || '', segment2 || ''),
    enabled: !!segment1 && !!segment2,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 1,
    refetchOnWindowFocus: false,
  });

  // Handle missing segments
  if (!segment1 || !segment2) {
    return {
      resolution: { type: 'notFound' },
      isLoading: false,
      error: null,
    };
  }

  // CRITICAL: Don't default to { type: 'notFound' } while loading
  // This was causing NotFound to flash before actual resolution
  return {
    resolution: isLoading ? undefined : (resolution || { type: 'notFound' }),
    isLoading,
    error: error as Error | null,
  };
}

// ============================================================================
// IMPERATIVE API
// ============================================================================

/**
 * Imperative two-segment resolution for use outside React components
 */
export async function resolveTwoSegmentImperative(
  segment1: string,
  segment2: string
): Promise<TwoSegmentResolution> {
  return resolveTwoSegment(segment1, segment2);
}
